<script lang="ts">
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import { addDollarSign, getNumBackInUnits, getPercentageFromBasisPoints } from '$lib/util/util';
	import BigNumber from 'bignumber.js';
	import {
		loadingPostTradeDetails,
		postTradeDetails,
		currentMakerBasisPoints,
		currentTakerBasisPoints,
		matchaProtocolFee
	} from '../store';
	import { executionType, input, isTPSLActive, stopLossInput, takeProfitInput } from './store';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';
	import { loadingMatchaCallData, matchaCallData } from '$lib/home/store';

	let expandFeeDetails = false;
	let expandTPSLFeeDetails = false;

	let isLimitOrder: boolean = false;
	let isTPSLOrder: boolean = false;

	$: if ($executionType == 'Limit' || $executionType == 'Stop') {
		isLimitOrder = true;
	} else {
		isLimitOrder = false;
	}
	$: isTPSLOrder = $isTPSLActive && ($takeProfitInput != '0' || $stopLossInput != '0');

	$: limitOrderFee =
		isLimitOrder && $postTradeDetails?.advancedOrderOrderFee
			? new BigNumber($input)
					.multipliedBy(getNumBackInUnits($postTradeDetails?.advancedOrderOrderFee, 18))
					.toString()
			: '0';
	$: tpslOrderFee =
		isTPSLOrder && $postTradeDetails?.advancedOrderOrderFee
			? new BigNumber($input)
					.multipliedBy(getNumBackInUnits($postTradeDetails?.advancedOrderOrderFee, 18))
					.toString()
			: '0';

	$: standardOrderFee = $postTradeDetails?.fee
		? new BigNumber($postTradeDetails.fee)
				.plus(new BigNumber($postTradeDetails?.keeperFee))
				.toString()
		: '';

	$: totalFee = new BigNumber(limitOrderFee)
		.plus(new BigNumber(standardOrderFee))
		.plus(isLimitOrder ? new BigNumber($postTradeDetails?.advancedOrderFixedFee || '0') : '0')
		.plus($matchaProtocolFee.toFixed(0))
		.toString();

	$: totalTPSLOrderFee = new BigNumber(tpslOrderFee)
		.plus(new BigNumber(standardOrderFee))
		.plus(isTPSLOrder ? new BigNumber($postTradeDetails?.advancedOrderFixedFee || '0') : '0')
		.toString();
</script>

<div class="border-t border-fx-grey-600 mb-2.5" />

<button
	class="flex w-full justify-between cursor-pointer mb-2"
	on:click={() => (expandFeeDetails = !expandFeeDetails)}
	on:keydown={() => (expandFeeDetails = !expandFeeDetails)}
>
	<div class="text-fx-grey-200 text-xs font-medium">
		{#if isLimitOrder}
			Approx. Fees
			<span class="text-fx-yellow">*</span>
		{:else}
			Fees
		{/if}
	</div>
	<div class="flex gap-1">
		{#if $loadingPostTradeDetails || $loadingMatchaCallData}
			<ProcessingIcon />
		{/if}

		{#if isLimitOrder}
			{#if $postTradeDetails?.fee}
				<div class="text-fx-grey-200 text-xs font-medium">
					${getNumBackInUnits(totalFee, 18, 2)}
				</div>
				<img
					src="/down-arrow.png"
					class="h-1.5 mt-1 transition duration-300 {expandFeeDetails ? 'rotate-180' : ''}"
					alt=""
				/>
			{:else}
				<div class="text-fx-grey-200 text-xs font-medium">$0</div>
				<img
					src="/down-arrow.png"
					class="h-1.5 mt-1 transition duration-300 {expandFeeDetails ? 'rotate-180' : ''}"
					alt=""
				/>
			{/if}
		{:else if $postTradeDetails?.fee}
			<div class="text-fx-grey-200 text-xs font-medium">
				${getNumBackInUnits(totalFee, 18, 2)}
			</div>
			<img
				src="/down-arrow.png"
				class="h-1.5 mt-1 transition duration-300 {expandFeeDetails ? 'rotate-180' : ''}"
				alt=""
			/>
		{:else}
			<div class="text-fx-grey-200 text-xs font-medium">$0</div>
			<img
				src="/down-arrow.png"
				class="h-1.5 mt-1 transition duration-300 {expandFeeDetails ? 'rotate-180' : ''}"
				alt=""
			/>
		{/if}
	</div>
</button>
{#if expandFeeDetails}
	{#if isLimitOrder}
		<div class="flex w-full justify-between pr-4 mt-1 mb-1">
			<div class="text-fx-grey-400 text-xs">
				Limit Fee ({new BigNumber($postTradeDetails?.advancedOrderOrderFee || '0').div(1e16)}%)
			</div>
			{#if limitOrderFee}
				<div class="text-fx-grey-400 text-xs">
					${getNumBackInUnits(limitOrderFee, 18, 4)}
				</div>
			{:else}
				<div class="text-fx-grey-400 text-xs">-</div>
			{/if}
		</div>
		<div class="flex w-full justify-between pr-4">
			<div class="text-fx-grey-400 text-xs">
				<Tooltip
					title="Fixed fee of ${getNumBackInUnits(
						$postTradeDetails?.advancedOrderFixedFee || '0',
						18,
						2
					)} for the limit order bots to execute the trade. Only paid if the limit order is executed."
					header="Limit Keeper Fees"
				>
					Limit Keeper Fees (Fixed)
				</Tooltip>
			</div>
			<Tooltip
				title="Fixed fee of ${getNumBackInUnits(
					$postTradeDetails?.advancedOrderFixedFee || '0',
					18,
					2
				)} for the limit order bots to execute the trade. Only paid if the limit order is executed."
				header="Limit Keeper Fees"
			>
				{#if $postTradeDetails}
					<div class="text-fx-grey-400 text-xs">
						${getNumBackInUnits($postTradeDetails?.advancedOrderFixedFee || '0', 18, 2)}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</Tooltip>
		</div>
	{/if}

	<div class="flex w-full justify-between pr-4 my-1">
		<Tooltip
			title="Fee of ${$postTradeDetails
				? getNumBackInUnits($postTradeDetails.keeperFee, 18, 2)
				: '-'} is given to the keeper bots to execute the trade, based on the current gas price. Only paid if the limit order is executed."
			header="Keeper Fees"
		>
			<div class="text-fx-grey-400 text-xs">Keeper Fees</div>
		</Tooltip>
		<Tooltip
			title="Fee of ${$postTradeDetails
				? getNumBackInUnits($postTradeDetails.keeperFee, 18, 2)
				: '-'} is given to the keeper bots to execute the trade, based on the current gas price. Only paid if the limit order is executed."
			header="Keeper Fees"
		>
			{#if $postTradeDetails}
				<div class="text-fx-grey-400 text-xs">
					${getNumBackInUnits($postTradeDetails.keeperFee, 18, 2)}
				</div>
			{:else}
				<div class="text-fx-grey-400 text-xs">-</div>
			{/if}
		</Tooltip>
	</div>

	<div class="flex w-full justify-between pr-4 mt-1 mb-4">
		<div class="text-fx-grey-400 text-xs">
			<Tooltip
				title={isLimitOrder
					? 'Maker/Taker fees can change and will be based on the market skew at the time of execution. Only paid if the limit order is executed.'
					: 'Maker/Taker fees based on the market skew.'}
				header="Fees"
			>
				{#if isLimitOrder}
					Approx. Fees ({getPercentageFromBasisPoints(
						$currentMakerBasisPoints
					)}/{getPercentageFromBasisPoints($currentTakerBasisPoints)}%)
					<span class="text-fx-yellow">*</span>
				{:else}
					Fees ({getPercentageFromBasisPoints(
						$currentMakerBasisPoints
					)}/{getPercentageFromBasisPoints($currentTakerBasisPoints)}%)
				{/if}
			</Tooltip>
		</div>

		<Tooltip
			title={isLimitOrder
				? 'Maker/Taker fees can change and will be based on the market skew at the time of execution. Only paid if the limit order is executed.'
				: 'Maker/Taker fees based on the market skew.'}
			header="Fees"
		>
			{#if $postTradeDetails?.fee}
				<div class="text-fx-grey-400 text-xs">
					${getNumBackInUnits($postTradeDetails.fee, 18, 4)}
				</div>
			{:else}
				<div class="text-fx-grey-100 text-xs">-</div>
			{/if}
		</Tooltip>
	</div>

	{#if $matchaCallData && Object.keys($matchaCallData).length !== 0}
		<div class="flex w-full justify-between pr-4 my-1">
			<Tooltip
				title="Swap Fees are paid to the liquidity providers of the exchange used."
				header="Swap Fees"
			>
				<div class="text-fx-grey-400 text-xs">Swap Fees</div>
			</Tooltip>
			<Tooltip
				title="Swap Fees are paid to the liquidity providers of the exchange used."
				header="Swap Fees"
			>
				<div class="text-fx-grey-400 text-xs">
					{addDollarSign(getNumBackInUnits($matchaProtocolFee.toFixed(0), 18, 2))}
				</div>
			</Tooltip>
		</div>
	{/if}
{/if}

{#if isTPSLOrder}
	<button
		class="flex w-full justify-between cursor-pointer mb-2"
		on:click={() => (expandTPSLFeeDetails = !expandTPSLFeeDetails)}
		on:keydown={() => (expandTPSLFeeDetails = !expandTPSLFeeDetails)}
	>
		<div class="flex gap-2">
			<div class="text-fx-grey-200 text-xs font-medium">TP/SL Fees</div>
			<div class="text-fx-grey-400 text-xs">(Upon Execution)</div>
		</div>

		<div class="flex gap-1">
			{#if $loadingPostTradeDetails}
				<ProcessingIcon />
			{/if}

			{#if $postTradeDetails?.fee}
				<div class="text-fx-grey-200 text-xs font-medium">
					${getNumBackInUnits(totalTPSLOrderFee, 18, 2)}
				</div>
				<img
					src="/down-arrow.png"
					class="h-1.5 mt-1 transition duration-300 {expandTPSLFeeDetails ? 'rotate-180' : ''}"
					alt=""
				/>
			{:else}
				<div class="text-fx-grey-200 text-xs font-medium">$0</div>
				<img
					src="/down-arrow.png"
					class="h-1.5 mt-1 transition duration-300 {expandTPSLFeeDetails ? 'rotate-180' : ''}"
					alt=""
				/>
			{/if}
		</div>
	</button>
	{#if expandTPSLFeeDetails}
		<div class="flex w-full justify-between pr-4 mt-1 mb-1">
			<div class="text-fx-grey-400 text-xs">TP/SL Fees (0.03%)</div>
			{#if tpslOrderFee}
				<div class="text-fx-grey-400 text-xs">
					${getNumBackInUnits(tpslOrderFee, 18, 4)}
				</div>
			{:else}
				<div class="text-fx-grey-400 text-xs">-</div>
			{/if}
		</div>
		<div class="flex w-full justify-between pr-4">
			<div class="text-fx-grey-400 text-xs">
				<Tooltip
					title="Fixed fee of ${getNumBackInUnits(
						$postTradeDetails?.advancedOrderFixedFee || '0',
						18,
						2
					)} for the TP/SL bots to execute the TP/SL order. Only paid if the TP/SL order is executed."
					header="TP/SL Keeper Fees"
				>
					TP/SL Keeper Fees (Fixed)
				</Tooltip>
			</div>
			<Tooltip
				title="Fixed fee of ${getNumBackInUnits(
					$postTradeDetails?.advancedOrderFixedFee || '0',
					18,
					2
				)} for the TP/SL bots to execute the TP/SL order. Only paid if the TP/SL order is executed."
				header="TP/SL Keeper Fees"
			>
				{#if $postTradeDetails}
					<div class="text-fx-grey-400 text-xs">
						${getNumBackInUnits($postTradeDetails?.advancedOrderFixedFee || '0', 18, 2)}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</Tooltip>
		</div>
		<div class="flex w-full justify-between pr-4 my-1">
			<Tooltip
				title="Fee given to the keeper bots to execute the TP/SL order, based on the gas price at the time of execution. Only paid if the TP/SL order is executed."
				header="TP/SL Keeper Fees"
			>
				<div class="text-fx-grey-400 text-xs">
					Keeper Fees <span class="text-fx-yellow">*</span>
				</div>
			</Tooltip>
			<Tooltip
				title="Fee given to the keeper bots to execute the TP/SL order, based on the gas price at the time of execution. Only paid if the TP/SL order is executed."
				header="TP/SL Keeper Fees"
			>
				{#if $postTradeDetails}
					<div class="text-fx-grey-400 text-xs">
						${getNumBackInUnits($postTradeDetails.keeperFee, 18, 2)}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</Tooltip>
		</div>
		<div class="flex w-full justify-between pr-4 my-1">
			<Tooltip
				title={'Maker/Taker fees can change and will be based on the market skew at the time of execution. Only paid if the TP/SL order is executed.'}
				header="TP/SL Fees"
			>
				<div class="text-fx-grey-400 text-xs">
					Approx. Fees ({getPercentageFromBasisPoints(
						$currentMakerBasisPoints
					)}/{getPercentageFromBasisPoints($currentTakerBasisPoints)}%)
					<span class="text-fx-yellow">*</span>
				</div>
			</Tooltip>
			<Tooltip
				title={'Maker/Taker fees can change and will be based on the market skew at the time of execution. Only paid if the TP/SL order is executed.'}
				header="TP/SL Fees"
			>
				{#if $postTradeDetails}
					<div class="text-fx-grey-400 text-xs">
						${getNumBackInUnits($postTradeDetails.fee, 18, 4)}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</Tooltip>
		</div>
	{/if}
{/if}
